import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, catchError, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ServiceBase } from './servicebase.service';
import { MessageService } from 'primeng/api';

@Injectable()
export class LoginService extends ServiceBase {
  static fieldDivisionsSubj: BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]> = new BehaviorSubject([]);
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = LoginService.fieldDivisionsSubj.asObservable();

  private _userInfoSource = new BehaviorSubject<ServerModels.UserInfoViewModel>(null);
  userInfoData$: Observable<ServerModels.UserInfoViewModel> = this._userInfoSource.asObservable();

  private _userPreferenceTypes = new ReplaySubject<ServerModels.ENESS_REF_UserPreferenceTypes[]>(1);
  userPreferenceTypes$: Observable<ServerModels.ENESS_REF_UserPreferenceTypes[]> = this._userPreferenceTypes.asObservable()

  constructor(protected http: HttpClient,
    public messageService: MessageService) {
    super(http, messageService);
    this.getFieldDivisions();
  }

  public getFieldDivisions() {
    this.http.get<ServerModels.ENESS_REF_FieldDivisions[]>(`${this.apiRoot}/api/Account/GetAllFieldDivisions`, this.httpNoAuth).pipe(
      take(1),
      catchError(this.swallowError<any>('getFieldDivisions', 'defaultMsg', null, null))
    ).subscribe((data: ServerModels.ENESS_REF_FieldDivisions[]) => LoginService.fieldDivisionsSubj.next(data));
  }

  public addUpdateUserProfile(profileChange: ServerModels.ENESS_XREF_UserProfileChangeRequests): Observable<ServerModels.UserInfoViewModel> {
    return this.http.post<ServerModels.UserInfoViewModel>(`${this.apiRoot}/api/Account/AddOrUpdateUserProfile`, profileChange, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addUpdateUserProfile', null, null, null)));
  }

  public verifyUserMFAToken(stateToken: string, code: string, factorId: string) : Observable<ServerModels.LoginReponse> {
    let data = {
      'stateToken': stateToken,
      'code': code,
      'factorId': factorId
    }
    let options = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    return this.http.post<ServerModels.LoginReponse>(`${this.apiRoot}${environment.oktaVerifyMFAUrl}`, data, {
      headers: options
    }).pipe(catchError(this.throwError<any>('verifyUserMFAToken', null, null, null)));;
  }

  public getAllRoles(): Observable<ServerModels.ENESS_ApplicationRole[]> {
    return this.http.get<ServerModels.ENESS_ApplicationRole[]>(`${this.apiRoot}/api/Account/GetAllRoles`, this.httpAuth)
      .pipe(catchError(this.swallowError<any>('getAllRoles', 'defaultMsg', null, null)));
  }

  public getUserClaims(): Observable<Claims.Claim[]> {
    return this.http.get<any[]>(`${this.apiRoot}/api/Account/UserClaims`, this.httpNoCache)
      .pipe(catchError(this.swallowError<any>('getUserClaims', 'defaultMsg', null, null)));
  }

  public getUserInfo(): Observable<ServerModels.UserInfoViewModel> {
    return this.http.get<ServerModels.UserInfoViewModel>(`${this.apiRoot}/api/Account/UserInfo`, this.httpNoCache)
      .pipe(catchError(this.swallowError<any>('getUserInfo', 'defaultMsg', null, null)));
  }

  public getAdmins(): Observable<ServerModels.UserInfoViewModel[]> {
    return this.http.get<ServerModels.UserInfoViewModel[]>(`${this.apiRoot}/api/Account/GetAdmins`, this.httpNoCache)
      .pipe(catchError(this.swallowError<any>('getAdmins', 'defaultMsg', null, null)));
  }

  public getPowerUsers(): Observable<ServerModels.UserInfoViewModel[]> {
    return this.http.get<ServerModels.UserInfoViewModel[]>(`${this.apiRoot}/api/Account/GetPowerUsers`, this.httpNoCache)
      .pipe(catchError(this.swallowError<any>('getPowerUsers', 'defaultMsg', null, null)));
  }

  public getUserTypes(): Observable<ServerModels.ENESS_REF_UserType[]> {
    return this.http.get<ServerModels.ENESS_REF_UserType[]>(`${this.apiRoot}/api/Account/GetUserTypes`, this.httpAuth)
      .pipe(catchError(this.swallowError<any>('getUserTypes', 'defaultMsg', null, null)));
  }

  public getJobTitles(): Observable<ServerModels.ENESS_REF_JobTitles[]> {
    return this.http.get<ServerModels.ENESS_REF_JobTitles[]>(`${this.apiRoot}/api/Account/GetJobTitles`, this.httpAuth)
      .pipe(catchError(this.swallowError<any>('getJobTitles', 'defaultMsg', null, null)));
  }

  public getAllFieldDivisionGroups(): Observable<ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[]> {
    return this.http.get<ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[]>(`${this.apiRoot}/api/Account/GetAllFieldDivisionGroups`, this.httpAuth)
      .pipe(catchError(this.swallowError<any>('getAllFieldDivisionGroups', 'defaultMsg', null, null)));
  }

  public getUserPreferenceTypes(): Observable<ServerModels.ENESS_REF_UserPreferenceTypes[]> {
    return this.http.get<ServerModels.ENESS_REF_UserPreferenceTypes[]>(`${this.apiRoot}/api/Account/GetUserPreferenceTypes`)
      .pipe(catchError(this.swallowError<any>('getUserPreferenceTypes', 'defaultMsg', null, null)));
  }

  public setUserPreference(userPreference: ServerModels.ENESS_ApplicationUserPreferences): Observable<ServerModels.ENESS_ApplicationUserPreferences> {
    return this.http.post<ServerModels.ENESS_ApplicationUserPreferences>(`${this.apiRoot}/api/Account/SetUserPreference`, userPreference, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('setUserPreference', 'defaultMsg', null, null))); 
  }

  public nextUserInfo(info: ServerModels.UserInfoViewModel) {
    this._userInfoSource.next(info);
  }

  public nextUserPreferenceType(types: ServerModels.ENESS_REF_UserPreferenceTypes[]): void {
    this._userPreferenceTypes.next(types);
  }

  public registerNewUser(registerViewModel: ServerModels.RegisterViewModel): Observable<ServerModels.RegisterViewModel> {
    let headersObj = this.httpAuth.headers;
    headersObj.set("Authorization", "Bearer " + localStorage.getItem(environment.authTokenStorageKey));
    return this.http.post<ServerModels.RegisterViewModel>(`${this.apiRoot}/api/Account/Register`, registerViewModel, { headers: headersObj })
      .pipe(catchError(this.throwError<any>('registerNewUser', 'defaultMsg', null, null)));
  }

  public login(sessionData: ServerModels.OktaUserSessionResponse): Observable<ServerModels.LoginReponse> {  
    let options = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    return this.http.post<ServerModels.LoginReponse>(`${this.apiRoot}/api/okta/login`, sessionData, { headers: options })
      .pipe(catchError(this.throwError<any>('login', null, null, null)));
  }

  public logout(refreshTokenId: string) {
    let data = {
      'refreshtoken': refreshTokenId
    }
    let options = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    return this.http.post<ServerModels.LoginReponse>(`${this.apiRoot}/api/okta/logout`, data, {
      headers: options
    }).pipe(catchError(this.throwError<any>('logout', null, null, null)));
  }

  public getOktaCurrentUserSession(){
      return this.http.get(`${environment.oktaBaseUrl}/api/v1/sessions/me`, { withCredentials: true })
          .pipe(catchError(this.swallowError<any>('getOktaCurrentUserSession', null, null, null))); 
  }
}
